import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle2,
  Clock3,
  MapPin,
  Menu as MenuIcon,
  Phone,
  ShieldCheck,
  Smartphone,
  Truck,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import InstallPWAButton from '@/components/InstallPWAButton';

const API_URL = 'https://targetexpress.secretofthedesert.com/api';

const defaultPlans = [
  {
    id: 1,
    name: 'الخطة الأساسية',
    basePrice: 15,
    description: 'ابدأ شحن طلبات متجرك داخل القاهرة والجيزة مع التتبع والتحصيل عند الاستلام.',
  },
  {
    id: 2,
    name: 'خطة النمو',
    basePrice: 12,
    description: 'وسّع تغطيتك إلى المحافظات مع معالجة أسرع وتقارير تشغيلية أوضح.',
  },
  {
    id: 3,
    name: 'خطة الشركات',
    basePrice: null,
    description: 'حل مخصص للأحجام الكبيرة مع مدير حساب وربط API حسب احتياجات نشاطك.',
  },
];

const features = [
  {
    icon: MapPin,
    title: 'تتبع واضح للشحنات',
    desc: 'تابع حالة كل شحنة من الإنشاء حتى التسليم من لوحة واحدة.',
  },
  {
    icon: Truck,
    title: 'تشغيل منظم',
    desc: 'حوّل الطلبات إلى خطوات تشغيل واضحة للاستلام والتجهيز والتوصيل.',
  },
  {
    icon: ShieldCheck,
    title: 'تحصيل عند الاستلام',
    desc: 'تابع مبالغ التحصيل والتسويات والتقارير المالية بسهولة أكبر.',
  },
  {
    icon: Smartphone,
    title: 'تحديثات للعملاء',
    desc: 'أرسل تحديثات حالة الشحنة عبر القنوات المتاحة في نظامك.',
  },
];

const steps = [
  { step: '01', title: 'أنشئ حسابك التجاري', desc: 'سجل بيانات نشاطك وابدأ من لوحة مصممة للتجارة الإلكترونية.' },
  { step: '02', title: 'أضف طلباتك', desc: 'أنشئ الشحنات يدويًا أو اربط متجرك أو استخدم ملفًا منظمًا.' },
  { step: '03', title: 'تابع التشغيل', desc: 'راقب التجهيز والتسليم والتحصيل من مكان واحد.' },
];

const stats = [
  { value: '+50,000', label: 'شحنة يتم توصيلها شهريًا' },
  { value: '+200', label: 'عميل يثقون بخدماتنا' },
  { value: '27', label: 'محافظة مغطاة' },
  { value: '95%', label: 'نسبة رضا العملاء' },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [pricingPlans, setPricingPlans] = useState(defaultPlans);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [pricingError, setPricingError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/PriceCategories`);
        if (!response.ok) throw new Error('pricing request failed');
        const json = await response.json();
        const plans = Array.isArray(json?.data)
          ? json.data
              .filter((plan: any) => plan?.isActive)
              .sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
              .slice(0, 3)
          : [];

        if (active && plans.length > 0) setPricingPlans(plans);
        if (active && plans.length === 0) setPricingError(true);
      } catch {
        if (active) setPricingError(true);
      } finally {
        if (active) setLoadingPricing(false);
      }
    };

    void fetchPricing();
    return () => {
      active = false;
    };
  }, []);

  const go = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" dir="rtl">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-slate-50/95 backdrop-blur">
        <div className="mx-auto flex min-h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 text-right"
            onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            aria-label="العودة إلى بداية الصفحة"
          >
            <img src="/logo-waigh-transparent.png" alt="Target Express" className="h-14 w-auto object-contain sm:h-16" />
            <span className="hidden min-w-0 sm:block">
              <strong className="block truncate text-lg leading-tight">Target Express</strong>
              <small className="block truncate text-xs text-slate-500">Shipping and Logistics Solutions</small>
            </span>
          </button>

          <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex" aria-label="التنقل الرئيسي">
            <button type="button" onClick={() => scrollTo('features')} className="hover:text-orange-600">المميزات</button>
            <button type="button" onClick={() => scrollTo('how-it-works')} className="hover:text-orange-600">كيف نعمل</button>
            <button type="button" onClick={() => scrollTo('pricing')} className="hover:text-orange-600">الأسعار</button>
            <button type="button" onClick={() => go('/faq')} className="hover:text-orange-600">الأسئلة الشائعة</button>
          </nav>

          <div className="hidden items-center gap-2 sm:flex">
            <Button variant="ghost" onClick={() => go('/login')}>تسجيل الدخول</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => go('/register')}>ابدأ كتاجر</Button>
          </div>

          <button
            type="button"
            className="rounded-lg p-2 text-slate-700 sm:hidden"
            onClick={() => setMenuOpen(value => !value)}
            aria-label={menuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 sm:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-2 text-right">
              <button type="button" className="rounded-lg px-3 py-3 text-right hover:bg-slate-50" onClick={() => { setMenuOpen(false); scrollTo('features'); }}>المميزات</button>
              <button type="button" className="rounded-lg px-3 py-3 text-right hover:bg-slate-50" onClick={() => { setMenuOpen(false); scrollTo('how-it-works'); }}>كيف نعمل</button>
              <button type="button" className="rounded-lg px-3 py-3 text-right hover:bg-slate-50" onClick={() => { setMenuOpen(false); scrollTo('pricing'); }}>الأسعار</button>
              <button type="button" className="rounded-lg px-3 py-3 text-right hover:bg-slate-50" onClick={() => go('/faq')}>الأسئلة الشائعة</button>
              <div className="mt-2 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3">
                <Button variant="outline" onClick={() => go('/login')}>تسجيل الدخول</Button>
                <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => go('/register')}>ابدأ كتاجر</Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.24),transparent_42%),radial-gradient(circle_at_bottom_left,rgba(37,99,235,0.24),transparent_42%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:py-28">
            <div className="text-center lg:text-right">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs text-slate-200">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                منصة شحن وتنفيذ مصممة للتجارة الإلكترونية
              </div>
              <h1 className="text-4xl font-black leading-[1.2] sm:text-5xl lg:text-6xl">
                اشحن طلبات متجرك
                <span className="block text-orange-500">من مكان واحد</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg lg:mx-0">
                أنشئ الشحنات، تابع حالات التسليم، أدر التحصيل عند الاستلام، وراجع أداء عملياتك من لوحة واحدة.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
                <Button size="lg" className="h-12 bg-orange-600 px-7 text-base hover:bg-orange-700" onClick={() => go('/register')}>
                  ابدأ حسابك التجاري
                </Button>
                <Button size="lg" variant="outline" className="h-12 border-white/30 bg-transparent px-7 text-base text-white hover:bg-white/10 hover:text-white" onClick={() => go('/track')}>
                  تتبع شحنتك
                </Button>
              </div>
              <button type="button" className="mt-5 text-sm text-slate-300 underline decoration-orange-500 underline-offset-4 hover:text-white" onClick={() => go('/contact')}>
                لديك حجم شحنات كبير؟ تحدث مع فريق المبيعات
              </button>
            </div>

            <div className="hidden lg:block">
              <div className="mx-auto max-w-md border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur">
                <div className="mb-5 flex items-center justify-between border-b border-white/10 pb-4">
                  <span className="text-sm text-slate-300">نظرة سريعة على التشغيل</span>
                  <BarChart3 className="h-5 w-5 text-orange-400" />
                </div>
                <div className="space-y-4">
                  {['طلب جديد', 'قيد التجهيز', 'خرج للتوصيل', 'تم التسليم'].map((label, index) => (
                    <div key={label} className="flex items-center gap-3">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${index === 3 ? 'bg-emerald-500' : 'bg-orange-600'}`}>{index + 1}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm"><span>{label}</span><span className="text-slate-400">{index === 3 ? 'مكتمل' : 'متابعة'}</span></div>
                        <div className="mt-2 h-1.5 bg-white/10"><div className={`h-full ${index === 3 ? 'w-full bg-emerald-400' : index === 2 ? 'w-3/4 bg-orange-400' : 'w-1/2 bg-orange-600'}`} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-8 sm:py-10">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 text-center sm:px-6 md:grid-cols-4 md:gap-8">
            {stats.map(stat => <div key={stat.label}><div className="text-2xl font-black text-orange-600 sm:text-4xl">{stat.value}</div><div className="mt-1 text-xs text-slate-600 sm:text-sm">{stat.label}</div></div>)}
          </div>
        </section>

        <section className="bg-slate-50 py-16 sm:py-20" id="features">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-14"><h2 className="text-3xl font-black sm:text-4xl">كل ما تحتاجه لتشغيل شحناتك</h2><p className="mt-4 leading-7 text-slate-600">أدوات عملية تساعدك على تقليل المتابعة اليدوية وتحسين وضوح العمليات.</p></div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{features.map(feature => { const Icon = feature.icon; return <Card key={feature.title} className="border-slate-200 bg-white shadow-sm"><CardContent className="p-6"><div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100 text-orange-600"><Icon className="h-6 w-6" /></div><h3 className="text-lg font-bold">{feature.title}</h3><p className="mt-2 leading-7 text-slate-600">{feature.desc}</p></CardContent></Card>; })}</div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20" id="how-it-works">
          <div className="mx-auto max-w-7xl px-4 sm:px-6"><div className="mx-auto mb-10 max-w-2xl text-center sm:mb-14"><h2 className="text-3xl font-black sm:text-4xl">ابدأ في ثلاث خطوات</h2><p className="mt-4 leading-7 text-slate-600">من التسجيل إلى متابعة الشحنة، كل خطوة واضحة وقابلة للقياس.</p></div><div className="grid gap-8 md:grid-cols-3">{steps.map(step => <div key={step.step} className="text-center"><div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-600 text-lg font-black text-white">{step.step}</div><h3 className="mt-5 text-xl font-bold">{step.title}</h3><p className="mx-auto mt-2 max-w-xs leading-7 text-slate-600">{step.desc}</p></div>)}</div></div>
        </section>

        <section className="bg-slate-50 py-16 sm:py-20" id="pricing">
          <div className="mx-auto max-w-7xl px-4 sm:px-6"><div className="mx-auto mb-8 max-w-2xl text-center"><h2 className="text-3xl font-black sm:text-4xl">خطة مناسبة لمرحلة نشاطك</h2><p className="mt-4 leading-7 text-slate-600">ابدأ بخطة واضحة، أو تواصل معنا إذا كان لديك حجم شحنات أو احتياجات خاصة.</p></div>{loadingPricing ? <div className="py-12 text-center text-slate-500">جاري تحميل الخطط...</div> : <><div className="mb-6 text-center text-sm text-slate-500">{pricingError ? 'نعرض الخطط الأساسية مؤقتًا. تواصل معنا للتأكد من السعر المناسب.' : 'الأسعار المعروضة إرشادية وتخضع لتفاصيل الشحنة والتغطية.'}</div><div className="grid gap-6 md:grid-cols-3">{pricingPlans.map((plan: any, index) => <Card key={plan.id} className={`border-slate-200 bg-white ${index === 1 ? 'ring-2 ring-orange-500' : ''}`}><CardContent className="p-7 text-center"><div className="mb-4 flex items-center justify-center gap-2 text-sm font-bold text-orange-600">{index === 1 && <CheckCircle2 className="h-4 w-4" />} {plan.name}</div><div className="text-3xl font-black text-slate-900">{plan.basePrice ? `${plan.basePrice} ج.م` : 'مخصص'}{plan.basePrice && <span className="text-sm font-normal text-slate-500"> / شحنة</span>}</div><p className="mt-4 min-h-[84px] leading-7 text-slate-600">{plan.description}</p><Button className="mt-5 w-full bg-orange-600 hover:bg-orange-700" onClick={() => go(plan.basePrice ? '/register' : '/contact')}>{plan.basePrice ? 'ابدأ الآن' : 'اطلب عرضًا'}</Button></CardContent></Card>)}</div></>}</div>
        </section>

        <section className="bg-orange-600 py-14 text-white sm:py-16"><div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 text-center sm:px-6 md:flex-row md:text-right"><div><h2 className="text-2xl font-black sm:text-3xl">جاهز لتنظيم شحناتك؟</h2><p className="mt-2 text-orange-100">ابدأ بحساب تجاري أو تحدث مع فريقنا عن احتياج نشاطك.</p></div><div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row"><Button className="bg-white text-orange-700 hover:bg-orange-50" onClick={() => go('/register')}>ابدأ كتاجر</Button><Button variant="outline" className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={() => go('/contact')}>تواصل معنا</Button></div></div></section>
      </main>

      <footer className="bg-slate-950 py-12 text-slate-300"><div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-4"><div><img src="/icon-512-from-server.png" alt="Target Express" className="h-16 w-auto object-contain" /><p className="mt-4 text-sm leading-7 text-slate-400">حلول شحن وتنفيذ تساعد المتاجر على إدارة الطلبات والتوصيل والتحصيل بوضوح.</p></div><div><h3 className="font-bold text-white">روابط سريعة</h3><div className="mt-4 space-y-3 text-sm"><button type="button" onClick={() => go('/track')} className="block hover:text-orange-400">تتبع شحنتك</button><button type="button" onClick={() => go('/about')} className="block hover:text-orange-400">من نحن</button><button type="button" onClick={() => go('/contact')} className="block hover:text-orange-400">اتصل بنا</button><a href="https://www.facebook.com/targetexpress" target="_blank" rel="noopener noreferrer" className="block hover:text-orange-400">صفحتنا على فيسبوك</a><a href="https://m.me/targetexpress" target="_blank" rel="noopener noreferrer" className="block hover:text-orange-400">كلّمنا على ماسنجر</a></div></div><div><h3 className="font-bold text-white">خدماتنا</h3><div className="mt-4 space-y-3 text-sm text-slate-400"><p>شحن داخلي</p><p>تحصيل عند الاستلام</p><p>تتبع الشحنات</p><p>تخزين وتجهيز</p></div></div><div><h3 className="font-bold text-white">تواصل معنا</h3><div className="mt-4 space-y-3 text-sm text-slate-400"><p className="flex items-center gap-2"><Phone className="h-4 w-4 text-orange-500" />01008524717</p><p className="flex items-center gap-2"><Users className="h-4 w-4 text-orange-500" />info@targetexpress.com</p><p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-orange-500" />القاهرة، مصر</p><p className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-orange-500" />دعم خلال ساعات العمل</p></div></div></div><div className="mx-auto mt-10 max-w-7xl border-t border-white/10 px-4 pt-6 text-center text-xs text-slate-500 sm:px-6">© 2026 Target Express. جميع الحقوق محفوظة.</div></footer>
      <InstallPWAButton />
    </div>
  );
}
